import React, { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import styled from "styled-components";
import usePopUpAtOnce from "components/hooks/usePopUpAtOnce";

const TYPE_OPTIONS = [
  { value: "novice", label: "Novice" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advance", label: "Advance" },
];

const CATEGORY_OPTIONS = [
  "Desarrolla tu idea",
  "Justifica tu respuesta",
  "Realiza una consulta",
  "Extiende el argumento",
  "Desafía una perspectiva",
  "Clarifica un punto",
];

const CreateSentenceStarterForm = ({
  handleUpdateOpenPopUp,
  handleGenerateSenentceStarter,
  savedLesson,
  hasErrorInContent,
  isAiPermissionEnabled,
  showAlert,
}) => {
  const { updatePopUpAtOnce } = usePopUpAtOnce();
  const [type, setType] = useState("novice");
  const [categories, setCategories] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryError, setCategoryError] = useState(false);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    const newCategories = typeof value === "string" ? value.split(",") : value;
    
    if (newCategories.includes("select-all")) {
      if (categories.length === CATEGORY_OPTIONS.length) {
        setCategories([]);
        setCategoryError(true);
      } else {
        setCategories([...CATEGORY_OPTIONS]);
        setCategoryError(false);
      }
      return;
    }
    
    setCategories(newCategories);
    if (newCategories.length > 0) {
      setCategoryError(false);
    }
  };

  const isAllSelected = categories.length === CATEGORY_OPTIONS.length;

  const handleGenerate = () => {
    if (!isAiPermissionEnabled) {
      showAlert("Please enable AI permission to create sentence starter.", "error");
      return;
    }
    if (categories.length === 0) {
      setCategoryError(true);
      showAlert("Please select at least one category.", "error");
      return;
    }
    const formData = { type, categories };
    if (savedLesson?.id && !hasErrorInContent) {
      handleUpdateOpenPopUp({ open: false, type: "" });
      handleUpdateOpenPopUp({ confirmationDialog: true, createFormData: formData });
    } else {
      handleUpdateOpenPopUp({ open: false, type: "" });
      handleGenerateSenentceStarter(formData);
    }
    updatePopUpAtOnce(false);
  };

  const handleClose = () => {
    handleUpdateOpenPopUp(
      { open: true, type: "sentenceStarterOption", confirmationDialog: false },
      "set"
    );
    updatePopUpAtOnce(false);
  };

  return (
    <div className="superLessionModal" style={{ zIndex: 14 }}>
      <ModalSuperPlane className="DifferentiationLessionContents createSentenceStarterForm sentence-starter-modal-container">
        <span onClick={handleClose}>
          <Cancel
            style={{
              position: "absolute",
              right: -7,
              top: -7,
              fontSize: 22,
              color: "red",
              background: "white",
              borderRadius: 20,
              cursor: "pointer",
            }}
          />
        </span>
        <FormControl
          fullWidth
          size="small"
          sx={{
            mt: 2,
            mb: 2,
            minWidth: 240,
            alignItems: "flex-start",
            "& .MuiOutlinedInput-root": { backgroundColor: "white" },
          }}
        >
          <Typography
            component="span"
            sx={{
              color: "white",
              display: "block",
              mb: 1,
              textAlign: "left",
              fontSize: "0.875rem",
            }}
          >
            Level
          </Typography>
          <Select
            value={type}
            onChange={(e) => setType(e.target.value)}
            onOpen={() => setMenuOpen(true)}
            onClose={() => setMenuOpen(false)}
            input={<OutlinedInput notched={false} />}
            MenuProps={{ disableScrollLock: true }}
            sx={{ width: "100%" }}
          >
            {TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          fullWidth
          size="small"
          sx={{
            mb: 2,
            minWidth: 240,
            alignItems: "flex-start",
            "& .MuiOutlinedInput-root": { backgroundColor: "white" },
          }}
          error={categoryError}
        >
          <Typography
            component="span"
            sx={{
              color: categoryError ? "#f44336" : "white",
              display: "block",
              mb: 1,
              textAlign: "left",
              fontSize: "0.875rem",
            }}
          >
            Quantity {categoryError && "*"}
          </Typography>
          <Select
            multiple
            value={categories}
            onChange={handleCategoryChange}
            onOpen={() => setMenuOpen(true)}
            onClose={() => setMenuOpen(false)}
            input={<OutlinedInput notched={false} />}
            displayEmpty
            renderValue={(selected) =>
              selected.length === 0
                ? <span style={{ color: '#999' }}>Select Quantity</span>
                : selected.length === CATEGORY_OPTIONS.length
                ? "All selected"
                : selected.join(", ")
            }
            MenuProps={{ disableScrollLock: true }}
            sx={{ width: "100%" }}
          >
            <MenuItem value="select-all">
              <Checkbox checked={isAllSelected} indeterminate={categories.length > 0 && !isAllSelected} />
              <ListItemText primary="Select All" />
            </MenuItem>
            {CATEGORY_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox checked={categories.indexOf(opt) > -1} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 16,
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <Button variant="contained" onClick={handleGenerate} style={{ width: "100%" }}>
            Create sentence starter
          </Button>
        </div>
      </ModalSuperPlane>
    </div>
  );
};

export default CreateSentenceStarterForm;

const ModalSuperPlane = styled.div`
  align-items: stretch;
  background-color: #000f4c;
  border-radius: 3px;
  display: inline-flex;
  flex-direction: column;
  left: 50%;
  max-width: 360px !important;
  padding: 24px 28px;
  position: absolute;
  text-align: left;
  top: 50%;
  transform: translate(-50%, -50%);
`;
